import '../styles/reset.css';
import '../styles/global.css';
import 'intersection-observer'; // polyfill for IntersectionObserver
import Cookie from 'js-cookie';

import getConfig from 'next/config';
import smoothscroll from 'smoothscroll-polyfill';
import { AppProps } from 'next/app';

import { Provider } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SWRConfig } from 'swr';
import { compose } from 'lodash/fp';
import { v4 as uuidv4 } from 'uuid';

import { AccountModalProvider } from 'account/providers/AccountModalProvider';
import { bugsnag } from 'shared/utils/services/bugsnag';
import BugsnagProvider from 'bugsnag/providers/BugsnagProvider';
import { ConstructorProvider } from 'search/providers/ConstructorProvider';
import { customCache } from 'homepage/utils/HomeTabCustomCache';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchCurrentUser } from 'auth/services/fetchCurrentUser';
import { getBugsnagApiKey, startBugsnag } from 'bugsnag/utils';
import { getUtmData } from 'shared/utils/getUtmData';
import { GlobalMetadata } from 'layout/head/GlobalMetadata';
import { homepageDataCache } from 'homepage/utils/HomepageDataAndPositionCache';
import { ICmsBanner } from 'banner/types/cmsBanner';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { ImpactScript } from 'tracking/components/ImpactScript';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { isMobileUserAgent as checkIsMobileUserAgent } from 'shared/utils/isMobileUserAgent';
import { MParticleProvider } from 'tracking/providers/MParticleProvider';
import { OneTrustScript } from 'tracking/components/OneTrustScript';
import { PageLayout } from 'layout/PageLayout';
import { requestService } from 'shared/utils/services/requestService';
import { SECURE_ROUTES } from 'shared/enums/SecureRoutes';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setFeatureFlags } from 'store/slice/featureFlagsSlice';
import { setIsUserAuthenticated, setUser, setStoreClientDedupID } from 'store/slice/authSlice';
import { setMenuNavigation } from 'store/slice/pageLayoutSlice';
import { SnapchatScript } from 'tracking/components/SnapchatScript';
import { startSession } from 'shared/services/startSession';
import store from 'store';
import { TiktokScript } from 'tracking/components/TiktokScript';
import {
  SNEAKER_LOCALE_COOKIE_NAME,
  AVAILABLE_LOCALES,
  DEFAULT_LOCALE,
  NEXT_LOCALE_COOKIE_NAME,
} from 'localization/constants/configEnums';
import withLocalization from 'shared/hocs/withLocalization';
import { STORAGE } from 'shared/enums/SitePreferences';
import { useI18nextClientSideReady } from 'localization/hooks/useI18nextReady';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { trackPerformance } from 'tracking/utils/performanceTracking';
import { IWebVitals } from 'tracking/types/WebVital';

interface IFeatureFlags {
  [key: string]: boolean;
}

interface IAppProps extends AppProps {
  cmsBanners: ICmsBanner[];
  cspNonce: string;
  featureFlags: IFeatureFlags;
  isAutomation: boolean;
  isCrawler: boolean;
  isInApp: boolean;
  isMobileUserAgent: boolean;
  isUserAuthenticated: boolean;
  menuNavigation: { calendar: ICMSBreadcrumb; navigation: ICMSBreadcrumb };
  referrer: string;
  siteCohortCookieValue: number;
  xGoatSite: string /* TODO: temporary for differentiating between staging.goat.com and goat-web labs. Can remove when we deprecate staging.goat.com */;
}

// TODO: probably move this elsewhere. Especially if it grows more complex
const swrFetcher = (url) => requestService({ url });

const pageWebVitals = new Map<IWebVitals, number>();

export function reportWebVitals(metric) {
  const isPagePerformanceEnabled =
    store.getState().featureFlags.featureFlags?.webEnablePagePerformanceAnalytics;
  if (!isPagePerformanceEnabled) return;

  pageWebVitals.set(metric.name, Math.round(metric.value * 100) / 100);
  if (document.readyState === 'complete' && pageWebVitals.size > 1) {
    trackPerformance(pageWebVitals);
  }
}

const App = ({
  cmsBanners,
  Component,
  cspNonce,
  featureFlags,
  isAutomation,
  isCrawler,
  isInApp,
  isMobileUserAgent,
  isUserAuthenticated,
  menuNavigation,
  pageProps,
  referrer,
  xGoatSite,
}: IAppProps) => {
  const router = useRouter();
  const { query, asPath, locale, events } = router;
  const utmData = getUtmData(query, referrer);
  const [bugsnagStarted, setBugsnagStarted] = useState(false);
  const [cacheStarted, setCacheStarted] = useState(false);
  const [clientDedupID, setClientDedupID] = useState(uuidv4());
  const {
    publicRuntimeConfig: { RELEASE_ENV },
  } = getConfig();
  const isNonDev = RELEASE_ENV !== 'development';
  const isImpactEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_IMPACT] && !isInApp;
  const isOneTrustEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_ONE_TRUST] && !isInApp;
  const isSnapchatEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_SNAPCHAT] && !isInApp;
  const isTiktokEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_TIKTOK] && !isInApp;
  const isPagePerformanceEnabled =
    !!featureFlags?.[FeatureFlags.WEB_ENABLE_PAGE_PERFORMANCE_ANALYTICS];
  const isConsumerSearchEnabled =
    !!featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  const initialRender = useRef(false);
  const firedCampaignSessionStart = useRef(false);

  if (!initialRender.current) {
    store.dispatch(setFeatureFlags(featureFlags));
    store.dispatch(setIsUserAuthenticated(isUserAuthenticated));
    store.dispatch(setMenuNavigation(menuNavigation));

    initialRender.current = true;
  }

  useI18nextClientSideReady();

  useEffect(() => {
    const refreshUser = async () => {
      const currentUser = await fetchCurrentUser();
      store.dispatch(setUser(currentUser));
    };

    if (isUserAuthenticated) {
      void refreshUser();
    }
  }, []);

  useEffect(() => {
    // init performance review for consumer
    try {
      const perfObserver = new PerformanceObserver((items) => {
        for (const entry of items.getEntries()) {
          const hasLongNetworkLatency = Math.round(entry.duration) > 15000;
          if (
            (entry.name === 'consumer-search' || entry.name === 'constructor-api') &&
            !hasLongNetworkLatency
          ) {
            void requestService({
              url: '/friendly-logs',
              method: 'POST',
              headers: {
                'x-goat-collection-api': entry.name,
                'x-goat-response-time': Math.round(entry.duration),
              },
            });
          }
        }
      });

      perfObserver.observe({ entryTypes: ['measure'] });
    } catch {
      // eslint-disable-next-line no-console
      console.log('PerformanceObserver is not supported');
    }

    /**
     * checks if the global middleware have been called
     * if not, we know we're viewing a cache version of the current page
     * and should start the session this includes populating country and currency cookies for now
     */
    const hasNoRegionCurrency = !Cookie.get(STORAGE.COUNTRY) || !Cookie.get(STORAGE.CURRENCY);
    if (hasNoRegionCurrency) {
      try {
        void startSession();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('region and currency cookies not set', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPagePerformanceEnabled) {
      const onDocumentFinish = () => {
        try {
          const observer = new PerformanceObserver((list) => {
            const entries: PerformanceNavigationTiming[] =
              list.getEntries() as PerformanceNavigationTiming[];
            for (const entry of entries) {
              const domContentLoaded =
                entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart;

              if (entry.domComplete > 0) {
                pageWebVitals.set('DOMInteractive', Math.round(entry.domInteractive * 100) / 100);
                pageWebVitals.set('DOMContentLoaded', Math.round(domContentLoaded * 100) / 100);
                pageWebVitals.set('DOMComplete', Math.round(entry.domComplete * 100) / 100);
                trackPerformance(pageWebVitals);
              }
            }
          });

          observer.observe({ entryTypes: ['navigation'] });
        } catch {
          // do nothing
        }
      };

      if (document.readyState === 'complete') {
        onDocumentFinish();
      } else {
        window.addEventListener('load', onDocumentFinish);
        return () => {
          window.removeEventListener('load', onDocumentFinish);
        };
      }
    }
  }, []);

  useEffect(() => {
    if (window) {
      smoothscroll.polyfill();
    }
  }, []);

  useEffect(() => {
    // tracks when GOAT.com session begins from a user clicking on a campaign link
    if (!firedCampaignSessionStart.current && utmData && bugsnagStarted && clientDedupID) {
      sendTrackingEvent('CAMPAIGN_SESSION_START', {
        ...utmData,
        client_dedup_id: clientDedupID,
        service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
      });
      firedCampaignSessionStart.current = true;
    }
  }, [utmData, bugsnagStarted, clientDedupID, firedCampaignSessionStart]);

  useEffect(() => {
    try {
      window?.mParticle.logPageView(
        'PageView',
        {
          hostname: window?.location.hostname,
          title: document?.title,
        },
        {
          'Google.Label': asPath,
          'Google.Page': asPath,
          'Google.Title': document?.title,
        },
      );
    } catch (error) {
      bugsnag(error, { context: 'Error logging page view to mParticle for Google' });
    }

    const storage = global.sessionStorage;
    if (!storage) return;

    const prevPath = storage.getItem('currentPath');
    storage.setItem('prevPath', prevPath);
    storage.setItem('currentPath', asPath);

    const lowerCasedLocaleCode = (locale || DEFAULT_LOCALE).toLowerCase();
    const sneakerLocaleCode = AVAILABLE_LOCALES?.[lowerCasedLocaleCode]?.locale;
    const nextLocaleCode = AVAILABLE_LOCALES?.[lowerCasedLocaleCode]?.code;
    Cookie.set(SNEAKER_LOCALE_COOKIE_NAME, sneakerLocaleCode);
    Cookie.set(NEXT_LOCALE_COOKIE_NAME, nextLocaleCode);

    if (!cacheStarted) {
      customCache.initialize();
      homepageDataCache.initialize();
      void setCacheStarted(true);
    }
  }, [asPath]);

  useEffect(() => {
    const newClientDedupID = uuidv4();
    store.dispatch(setStoreClientDedupID(newClientDedupID));
    events.on('routeChangeComplete', () => setClientDedupID(newClientDedupID));

    return () => {
      events.off('routeChangeComplete', () => setClientDedupID(newClientDedupID));
    };
  }, [events]);

  if (getBugsnagApiKey() && !bugsnagStarted) {
    void startBugsnag();
    void setBugsnagStarted(true);
  }

  return (
    <>
      <GlobalMetadata />
      <Provider store={store}>
        <BugsnagProvider>
          {(isNonDev || isAutomation) && !isCrawler && (
            <>
              {isOneTrustEnabled && !isAutomation && !isInApp && (
                <OneTrustScript cspNonce={cspNonce} />
              )}
              {isImpactEnabled && (
                <ImpactScript isOneTrustEnabled={isOneTrustEnabled} cspNonce={cspNonce} />
              )}
              {isTiktokEnabled && (
                <TiktokScript isOneTrustEnabled={isOneTrustEnabled} cspNonce={cspNonce} />
              )}
            </>
          )}
          <SWRConfig
            value={{
              fetcher: swrFetcher,
            }}
          >
            {/* NOTE: Snapchat script located here to get access to auth provider */}
            {isSnapchatEnabled && (isNonDev || isAutomation) && !isCrawler && (
              <SnapchatScript
                isOneTrustEnabled={isOneTrustEnabled}
                cspNonce={cspNonce}
                clientDedupID={clientDedupID}
                isUserAuthenticated={isUserAuthenticated}
              />
            )}
            {/* TODO: move mParticle out into its own script rather than a provider  */}
            <MParticleProvider clientDedupID={clientDedupID}>
              <ConstructorProvider isConsumerSearchEnabled={isConsumerSearchEnabled}>
                <AccountModalProvider>
                  <PageLayout
                    cmsBanners={cmsBanners}
                    isCrawler={isCrawler}
                    isInApp={isInApp}
                    isMobileUserAgent={isMobileUserAgent}
                    xGoatSite={xGoatSite}
                  >
                    <Component {...pageProps} />
                  </PageLayout>
                </AccountModalProvider>
              </ConstructorProvider>
            </MParticleProvider>
          </SWRConfig>
        </BugsnagProvider>
      </Provider>
    </>
  );
};

App.getInitialProps = async ({ Component, ctx }) => {
  let cmsBanners = [];
  let cspNonce = '';
  let featureFlags: any = { webEnableGoatBanner: false };
  let isAutomation = false;
  let isCrawler = false;
  let isInApp = false;
  let isMobileUserAgent = true;
  let isUserAuthenticated = false;
  let menuNavigation = {};
  let pageProps = {};
  let referrer = null;
  let xGoatSite = null;

  if (ctx.res?.locals?.user) {
    isUserAuthenticated = !!ctx.res.locals.user?.authToken;
  }
  if (ctx.res?.locals?.cspNonce) {
    cspNonce = ctx.res.locals.cspNonce;
  }

  // Keep this on top to prevent unused api calls
  if (SECURE_ROUTES.has(ctx.pathname) && !isUserAuthenticated) {
    const encodedPath = encodeURIComponent(ctx.asPath) || '/';
    ctx.res?.writeHead(302, {
      Location: getRedirectionPath(`/login?redirect=${encodedPath}`, ctx?.locale),
    });
    ctx.res?.end();
    return {};
  }

  if (ctx.req) {
    // server side rendered data
    featureFlags = ctx.req.featureFlags || {};
    // featureFlags.tempWebEnableSearchMicrofrontend = true;
    cmsBanners = ctx.req.cmsBanner?.sections;
    referrer = ctx.req.headers.referer;
    xGoatSite = ctx.req.headers?.['x-goat-site'];
    isAutomation = ctx.req.cookies?.is_goat_automation === 'true';
    isCrawler = isSearchCrawler(ctx.req.headers);
    isInApp = ctx.req.query?.app === 'y';
    menuNavigation = ctx.req.menuNavigation;
    const userAgent = ctx.req?.headers?.['user-agent'];
    isMobileUserAgent = checkIsMobileUserAgent(userAgent);
  } else {
    // TODO: we need to be able to serve this from the cookie and not refetch everytime
    // blocker: some server side code do require it which is an anti pattern
    // and they are app/route level middlewares (guest cohort and payment for klarna)
    // unfortunately we can't subscribe to the feature flags service to listen to changes
    // so we have to do this gimmick of server side rendering / client side or middleware call
    const flagResponse = await fetch('/web-api/feature-flags');
    featureFlags = await flagResponse.json();
    featureFlags = {
      ...(featureFlags || {}),
      // tempWebEnableSearchMicrofrontend: true,
    };
    if (featureFlags?.webEnableGoatBanner) {
      try {
        const response = await fetch('/web-api/v1/cms/cms_articles/web-cta-banner');
        const responseJSON = await response.json();
        cmsBanners = responseJSON?.sections;
      } catch (error) {
        if (typeof error !== 'undefined') {
          bugsnag(error, { context: 'Fetching Web CTA Banner' });
        }
      }
    }
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  return {
    cmsBanners,
    cspNonce,
    featureFlags,
    isAutomation,
    isCrawler,
    isInApp,
    isMobileUserAgent,
    isUserAuthenticated,
    menuNavigation,
    pageProps,
    referrer,
    xGoatSite,
  };
};

const enhance = compose(withLocalization);

export default enhance(App);
